import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const cardVariants = {
  hidden: { 
    opacity: 0, 
    x: -50,
    rotateY: -20
  },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    rotateY: 0,
    transition: {
      delay: i * 0.2,
      duration: 0.6,
      type: "spring",
      stiffness: 80
    }
  })
};

const starVariants = {
  hidden: { scale: 0, rotate: -180 },
  visible: (i) => ({
    scale: 1,
    rotate: 0,
    transition: {
      delay: 0.3 + i * 0.1,
      type: "spring",
      stiffness: 200
    }
  })
};

export function Testimonials() {
  const { t, isRTL } = useLanguage();
  const reviews = t('testimonials.reviews');

  return (
    <section 
      className="py-24 sm:py-32 bg-[#FDF8F4] overflow-hidden"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.h2 
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 100 }}
            className={`font-cormorant text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#6B1C23] ${
              isRTL ? 'font-noto-serif-arabic' : ''
            }`}
            data-testid="testimonials-title"
          >
            {t('testimonials.title')}
          </motion.h2>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="h-1 w-24 bg-[#6B1C23] mx-auto mt-4"
          />
        </motion.div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" style={{ perspective: "1000px" }}>
          {reviews.map((review, index) => (
            <motion.div
              key={review.id}
              custom={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={cardVariants}
              whileHover={{ 
                y: -10, 
                boxShadow: "0 25px 50px rgba(107, 28, 35, 0.15)",
                transition: { duration: 0.3 }
              }}
              className="relative bg-white border border-gray-200 p-8 rounded-sm shadow-md"
              data-testid={`testimonial-${index}`}
              style={{ transformStyle: "preserve-3d" }}
            >
              {/* Animated Quote Icon */}
              <motion.div
                initial={{ opacity: 0, scale: 0, rotate: -45 }}
                whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, type: "spring" }}
                className="absolute top-6 right-6"
              >
                <Quote className="w-10 h-10 text-[#6B1C23]/15" />
              </motion.div>
              
              {/* Animated Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(review.rating)].map((_, i) => (
                  <motion.div
                    key={i}
                    custom={i}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={starVariants}
                  >
                    <Star className="w-5 h-5 fill-[#E8A838] text-[#E8A838]" />
                  </motion.div>
                ))}
              </div>
              
              {/* Review Text with Fade */}
              <motion.p 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className={`font-manrope text-[#4A4A4A] mb-6 leading-relaxed ${isRTL ? 'font-cairo' : ''}`}
              >
                "{review.text}"
              </motion.p>
              
              {/* Reviewer Name with Slide */}
              <motion.p 
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
                className={`font-cormorant text-lg text-[#6B1C23] font-semibold ${isRTL ? 'font-noto-serif-arabic' : ''}`}
              >
                — {review.name}
              </motion.p>

              {/* Bottom Accent Line */}
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7, duration: 0.5 }}
                className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#6B1C23] to-[#D4A574] origin-left"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
